import { useState } from 'react'
import { Field, TextInput } from '@/components/explore/CreatorFields'
import AddressAutocomplete from '@/components/explore/AddressAutocomplete'
import LocationPicker, {
  type Coordinates,
} from '@/components/school/LocationPicker'
import type { FormErrors, SetField, WizardForm } from './types'

/**
 * Passo 2 — nome e onde fica.
 *
 * PLACEHOLDERS INSTRUCIONAIS, e não exemplos. "Ex.: Junco Surf School" no campo
 * do próprio nome punha o nome de um concorrente na cara de quem está
 * cadastrando, e "Fortaleza" na cidade sugeria resposta em vez de pedir a
 * dela. O placeholder diz O QUE digitar; o formato, quando importa, vai na
 * dica embaixo do campo.
 */
export default function StepBusiness({
  data,
  setData,
  errors,
}: {
  data: WizardForm
  setData: SetField
  errors: FormErrors
}) {
  const [geocoding, setGeocoding] = useState(false)
  const [geocodeError, setGeocodeError] = useState<string | null>(null)

  const locateOnMap = async () => {
    setGeocoding(true)
    setGeocodeError(null)
    try {
      const response = await fetch('/geocodificar', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-CSRF-Token':
            document
              .querySelector('meta[name="csrf-token"]')
              ?.getAttribute('content') ?? '',
        },
        body: JSON.stringify({
          address: data.address,
          city: data.city,
          state: data.state,
          postal_code: data.postal_code,
        }),
      })
      const body = await response.json()
      if (body.success) {
        setData('latitude', body.latitude)
        setData('longitude', body.longitude)
      } else {
        setGeocodeError(body.error ?? 'Não foi possível localizar o endereço.')
      }
    } catch {
      setGeocodeError(
        'Não foi possível localizar agora. Marque o ponto no mapa.',
      )
    } finally {
      setGeocoding(false)
    }
  }

  const coords: Coordinates | null =
    data.latitude != null && data.longitude != null
      ? { lat: data.latitude, lng: data.longitude }
      : null

  return (
    <div className="space-y-4">
      <Field label="Nome do negócio" required error={errors.name}>
        <TextInput
          value={data.name}
          onChange={(v) => setData('name', v)}
          placeholder="Digite o nome do seu negócio"
          invalid={Boolean(errors.name)}
        />
      </Field>

      {/* O endereço vem da busca, não de quatro campos digitados no escuro:
          escolher uma sugestão preenche rua, CEP, cidade, UF e já derruba o
          pino no mapa. Os campos abaixo continuam editáveis para o número da
          casa e para quem mora onde o OpenStreetMap não chega. */}
      <Field
        label="Endereço"
        hint="Comece a digitar e escolha na lista — o resto preenche sozinho."
      >
        <AddressAutocomplete
          value={data.address}
          onChange={(v) => setData('address', v)}
          onSelect={(suggestion) => {
            setData('address', suggestion.address)
            if (suggestion.postal_code)
              setData('postal_code', suggestion.postal_code)
            if (suggestion.city) setData('city', suggestion.city)
            if (suggestion.state) setData('state', suggestion.state)
            setData('latitude', suggestion.latitude)
            setData('longitude', suggestion.longitude)
          }}
          placeholder="Digite a rua e o número"
        />
      </Field>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <Field label="CEP" hint="00000-000">
          <TextInput
            value={data.postal_code}
            onChange={(v) => setData('postal_code', v)}
            placeholder="Digite seu CEP"
            inputMode="numeric"
          />
        </Field>
        <Field label="Cidade">
          <TextInput
            value={data.city}
            onChange={(v) => setData('city', v)}
            placeholder="Digite sua cidade"
          />
        </Field>
        <Field label="Estado" hint="A sigla, com 2 letras">
          <TextInput
            value={data.state}
            onChange={(v) => setData('state', v.toUpperCase().slice(0, 2))}
            placeholder="Digite a UF"
          />
        </Field>
      </div>

      {/* Saída manual: endereço digitado à mão, sem passar pela lista. */}
      <button
        type="button"
        onClick={locateOnMap}
        disabled={geocoding}
        className="rounded-full border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 transition-colors hover:bg-gray-50 disabled:opacity-60"
      >
        {geocoding ? 'Localizando…' : 'Localizar no mapa'}
      </button>
      {geocodeError && <p className="text-xs text-red-600">{geocodeError}</p>}

      <LocationPicker
        value={coords}
        onChange={(next) => {
          setData('latitude', next.lat)
          setData('longitude', next.lng)
        }}
        hint="Clique no mapa ou arraste o marcador para ajustar o ponto exato do seu negócio."
      />
    </div>
  )
}
